<template>
  <div class="page-wrapper">
    <!-- Modern Header -->
    <header class="header">
      <div class="header-container">
        <router-link to="/" class="logo-link">
          <img src="@/assets/logo.png" alt="Nexthome4U Logo" class="logo" />
        </router-link>
      </div>
    </header>

    <!-- Main Content -->
    <main class="main-content">
      <div class="reset-card">
        <div class="card-header">
          <h1 class="title">Reset Your Password</h1>
          <p class="subtitle">Create a new secure password for your account</p>
        </div>

        <form class="form" @submit.prevent="submitForm">
          <div class="form-group">
            <label for="newPassword" class="label">New Password</label>
            <div class="input-wrapper">
              <input 
                :type="showNewPassword ? 'text' : 'password'" 
                id="newPassword" 
                v-model="newPassword" 
                class="input"
                :class="{ 'input-error': errorMessage }"
                placeholder="Enter new password"
                required 
                autocomplete="new-password"
              />
              <button 
                type="button" 
                class="password-toggle"
                @click="showNewPassword = !showNewPassword"
                tabindex="-1"
              >
                <svg v-if="!showNewPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
          </div>

          <div class="form-group">
            <label for="confirmPassword" class="label">Confirm Password</label>
            <div class="input-wrapper">
              <input 
                :type="showConfirmPassword ? 'text' : 'password'" 
                id="confirmPassword" 
                v-model="confirmPassword" 
                class="input"
                :class="{ 'input-error': errorMessage }"
                placeholder="Confirm new password"
                required 
                autocomplete="new-password"
              />
              <button 
                type="button" 
                class="password-toggle"
                @click="showConfirmPassword = !showConfirmPassword"
                tabindex="-1"
              >
                <svg v-if="!showConfirmPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
          </div>

          <div v-if="errorMessage" class="error-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            {{ errorMessage }}
          </div>

          <div class="button-group">
            <button type="submit" class="submit-button" :disabled="isSubmitting">
              <span v-if="!isSubmitting">Update Password</span>
              <div v-if="isSubmitting" class="loading-spinner">
                <div class="spinner"></div>
                <span>Updating...</span>
              </div>
            </button>
            
            <button type="button" class="cancel-button" @click="cancel">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </main>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { waitForToken } from '@/utilities/tokenHelper';
import { isNoDataError, isHmsSubscriptionInactiveError } from '@/utilities/errorHelper';

export default {
  name: 'ResetPasswordPage',
  data() {
    return {
      newPassword: '',
      confirmPassword: '',
      showNewPassword: false,
      showConfirmPassword: false,
      errorMessage: '',
      isSubmitting: false,
    };
  },
  methods: {
    async submitForm() {
      // Clear any previous error message
      this.errorMessage = '';

      // Validate that passwords match
      if (this.newPassword !== this.confirmPassword) {
        this.errorMessage = 'Passwords do not match';
        return;
      }

      // Set submitting state
      this.isSubmitting = true;

      try {
        // API call to reset password
        await waitForToken() // waits for token
        const response = await apiService.resetPassword(
          this.$route.query.email,
          this.$route.query.otp,
          this.newPassword
        );

        // Check API response
        if (response.data.success) {
          this.$router.push('/reset/success'); // Navigate to success component
        } else {
          this.$router.push('/reset/failed');  // Navigate to failed component
        }
      } catch (error) {
        console.error('Reset password error:', error);
        // Skip error handling for "no data" cases (status 400 with errorcode 2003)
        if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
          return;
        }
        
        let message = null;
        if (error?.response?.data) {
          const errorData = error.response.data;
          const status = error.response.status;
          
          // For HTTP 400 errors, always use the reason field from API response
          if (status === 400) {
            if (errorData.reason) {
              message = errorData.reason;
            } else if (errorData.message) {
              message = errorData.message;
            }
            // If no reason/message provided for 400 error, don't show error (no generic messages)
          } else {
            // For other status codes, prioritize reason field
            if (errorData.reason) {
              message = errorData.reason;
            } else if (errorData.message) {
              message = errorData.message;
            } else {
              message = 'Failed to reset password. Please try again.';
            }
          }
        } else {
          message = 'Failed to reset password. Please try again.';
        }
        
        // Only set error message if we have a message (genuine errors only)
        if (message) {
          this.errorMessage = message;
        }
      } finally {
        this.isSubmitting = false; // Reset submitting state
      }
    },
    cancel() {
      this.$router.push('/login'); // Navigate back to login page
    },
  },
};
</script>

<style scoped>

* {
  box-sizing: border-box;
}

.page-wrapper {
  min-height: 100vh;
  background: #fcfbfb;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  flex-direction: column;
}

/* Header */
.header {
  position: sticky;
  top: 0;
  left: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 1rem 0;
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.logo-link {
  display: inline-block;
  transition: transform 0.2s ease;
}

.logo-link:hover {
  transform: scale(1.05);
}

.logo {
  height: 40px;
  width: auto;
  max-width: 150px;
}

/* Main Content */
.main-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  min-height: calc(100vh - 80px);
}

.reset-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 
    0 32px 64px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  padding: 3rem;
  width: 100%;
  max-width: 420px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.card-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  letter-spacing: -0.025em;
}

.subtitle {
  color: #6b7280;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
}

/* Form */
.form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.25rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input {
  width: 100%;
  padding: 0.875rem 1rem;
  padding-right: 3rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 400;
  color: #1f2937;
  background: #ffffff;
  transition: all 0.2s ease;
  outline: none;
}

.input:focus {
  border-color: #5b2e8e;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
  background: #ffffff;
}

.input:hover {
  border-color: #d1d5db;
}

.input-error {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.input::placeholder {
  color: #9ca3af;
  font-weight: 400;
}

.password-toggle {
  position: absolute;
  right: 1rem;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-toggle:hover {
  color: #5b2e8e;
  background: rgba(91, 46, 142, 0.1);
}

.error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #ef4444;
  font-size: 0.875rem;
  font-weight: 500;
  background: rgba(239, 68, 68, 0.1);
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.button-group {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
}

.submit-button {
  flex: 1;
  background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 0.875rem 2.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 48px;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.4);
  white-space: nowrap;
  min-width: 0;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(91, 46, 142, 0.4);
}

.submit-button:active {
  transform: translateY(0);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.2);
}

.cancel-button {
  flex: 1;
  background: white;
  color: #5b2e8e;
  border: 2px solid #5b2e8e;
  border-radius: 12px;
  padding: 0.875rem 2.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 48px;
  white-space: nowrap;
  min-width: 0;
}

.cancel-button:hover {
  background: #5b2e8e;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Design */
@media (max-width: 640px) {
  .main-content {
    padding: 1rem;
    align-items: flex-start;
    padding-top: 2rem;
  }

  .reset-card {
    padding: 2rem 1.5rem;
    border-radius: 16px;
    margin-top: 1rem;
  }

  .title {
    font-size: 1.75rem;
  }

  .button-group {
    flex-direction: column;
    gap: 0.75rem;
  }

  .submit-button,
  .cancel-button {
    font-size: 0.9rem;
    padding: 0.75rem 1.5rem;
  }
}

@media (max-width: 480px) {
  .reset-card {
    padding: 1.5rem 1rem;
  }

  .title {
    font-size: 1.5rem;
  }

  .subtitle {
    font-size: 0.925rem;
  }
}
</style>